// ============================================================================
// REACH SERVICE
// All API calls for the REACH change management module
// ============================================================================

import { api } from './api';

export type ReachStage = 'R' | 'E' | 'A' | 'C' | 'H';
export type ReachScore = 1 | 2 | 3 | 4;
export type AdoptionCurve = 'innovator' | 'early_adopter' | 'early_majority' | 'late_majority' | 'sceptic';
export type AssessmentType = 'champion' | 'executive' | 'practice-leader' | 'user' | 'general';
export type ApprovalStatus = 'pending' | 'approved' | 'rejected';
export type BlockerType = 'individual' | 'team' | 'structural';
export type BlockerStatus = 'open' | 'in-progress' | 'resolved';
export type ExtractionStatus = 'generating' | 'pending-review' | 'confirmed' | 'dismissed';

export interface ReachEngagementConfig {
  programmeName: string;
  customerOrg: string;
  sponsorName: string;
  commsOwner: string;
  fde: string;
  engagementDurationWeeks: 6 | 8 | 12;
  startDate: string;
  stageWeekMapping?: Record<string, number[]>;
  terminologyOverrides?: Record<string, string>;
  toneGuide?: string;
  glossary?: Array<{ term: string; definition: string }>;
  sensitiveContactsIncludeInExport?: boolean;
  configuredAt: string;
  configuredBy: string;
  updatedAt: string;
}

export interface ReachStakeholder {
  id: string;
  name: string;
  role: string;
  department: string;
  color: string;
  x: number;
  y: number;
  adoptionCurve: AdoptionCurve;
  createdAt: string;
  updatedAt: string;
}

export interface ReachChannel {
  id: string;
  name: string;
  reachStage: ReachStage;
  stakeholderIds: string[];
  createdAt: string;
}

export interface ReachAssessment {
  id: string;
  respondentName: string;
  role: string;
  type: AssessmentType;
  level?: string;
  practice?: string;
  business?: string;
  timestamp: string;
  individual: Record<'r' | 'e' | 'a' | 'c' | 'h', ReachScore>;
  organisational: Record<'r' | 'e' | 'a' | 'c' | 'h', ReachScore>;
}

export interface ReachPulseEntry {
  week: number;
  saw: string[];
  heard: string[];
  needHelp: string[];
  updatedAt: string;
  updatedBy: string;
}

export interface ReachTrackerEntry {
  week: number;
  scores: Partial<Record<
    'r_individual' | 'r_org' | 'e_individual' | 'e_org' |
    'a_individual' | 'a_org' | 'c_individual' | 'c_org' |
    'h_individual' | 'h_org',
    ReachScore
  >>;
  notes?: string;
  updatedAt: string;
  updatedBy: string;
}

export interface ReachPlaybookEntry {
  week: number;
  reachStage: ReachStage;
  cmActivities: string;
  comms: string;
  whatToNotice: string;
  sponsorAsks: string;
  guardrail: string;
  isCustomised: boolean;
  customisedBy?: string;
  customisedAt?: string;
}

export interface ReachCommsItem {
  id: string;
  item: string;
  audience: string;
  draftedBy: string;
  approver: string;
  approvalStatus: ApprovalStatus;
  approvedAt?: string;
  sendDate: string;
  channel: string;
  notes?: string;
  reachStage: ReachStage;
  createdAt: string;
  createdBy: string;
}

export interface ReachBlocker {
  id: string;
  type: BlockerType;
  description: string;
  raisedInWeek: number;
  status: BlockerStatus;
  action?: string;
  owner?: string;
  dueDate?: string;
  aiSuggested: boolean;
  aiSuggestionSource?: 'pulse' | 'tracker' | 'both';
  confirmedByPractitioner: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ExtractionDraft {
  stakeholders: Array<{ name: string; role: string; function: string; influence: number; readiness: number; signals: string[] }>;
  changeImpacts: Array<{ function: string; currentState: string; futureState: string; impactRating: 1 | 2 | 3 | 4 }>;
  blockers: Array<{ description: string; type: BlockerType }>;
  championCandidates: Array<{ name: string; role: string; rationale: string }>;
  isEmpty: boolean;
}

export interface ReachExtraction {
  sessionId: string;
  status: ExtractionStatus;
  requestId: string;
  generatedAt?: string;
  confirmedAt?: string;
  confirmedBy?: string;
  draft?: ExtractionDraft;
  confirmed?: ExtractionDraft;
}

export interface ReachPlanSection {
  id: string;
  title: string;
  content: string | null;
  editedContent: string | null;
  hasSufficientData: boolean;
  guidance: string | null;
  lastGeneratedAt: string | null;
}

export interface ReachAdoptionPlan {
  sections: ReachPlanSection[];
  status: 'empty' | 'partial' | 'complete';
  generatedAt: string;
}

// ── API helpers ────────────────────────────────────────────────────────────

function base(projectId: string) {
  return `/api/projects/${projectId}/reach`;
}

// ── Config ─────────────────────────────────────────────────────────────────

export const reachConfigAPI = {
  get: (projectId: string) =>
    api.get<{ config: ReachEngagementConfig | null }>(`${base(projectId)}/config`),
  put: (projectId: string, config: Partial<ReachEngagementConfig>) =>
    api.put<{ config: ReachEngagementConfig }>(`${base(projectId)}/config`, config)
};

// ── Stakeholders ───────────────────────────────────────────────────────────

export const reachStakeholdersAPI = {
  list: (projectId: string) =>
    api.get<{ stakeholders: ReachStakeholder[] }>(`${base(projectId)}/stakeholders`),
  create: (projectId: string, data: Omit<ReachStakeholder, 'id' | 'createdAt' | 'updatedAt'>) =>
    api.post<{ stakeholder: ReachStakeholder }>(`${base(projectId)}/stakeholders`, data),
  update: (projectId: string, id: string, data: Partial<ReachStakeholder>) =>
    api.patch<{ stakeholder: ReachStakeholder }>(`${base(projectId)}/stakeholders/${id}`, data),
  updatePosition: (projectId: string, id: string, x: number, y: number) =>
    api.patch(`${base(projectId)}/stakeholders/${id}/position`, { x, y }),
  delete: (projectId: string, id: string) =>
    api.delete(`${base(projectId)}/stakeholders/${id}`)
};

// ── Channels ───────────────────────────────────────────────────────────────

export const reachChannelsAPI = {
  list: (projectId: string) =>
    api.get<{ channels: ReachChannel[] }>(`${base(projectId)}/channels`),
  create: (projectId: string, data: Omit<ReachChannel, 'id' | 'createdAt'>) =>
    api.post<{ channel: ReachChannel }>(`${base(projectId)}/channels`, data),
  update: (projectId: string, id: string, data: Partial<ReachChannel>) =>
    api.patch<{ channel: ReachChannel }>(`${base(projectId)}/channels/${id}`, data),
  delete: (projectId: string, id: string) =>
    api.delete(`${base(projectId)}/channels/${id}`)
};

// ── Assessments ────────────────────────────────────────────────────────────

export const reachAssessmentsAPI = {
  list: (projectId: string) =>
    api.get<{ assessments: ReachAssessment[] }>(`${base(projectId)}/assessments`),
  submit: (projectId: string, data: Omit<ReachAssessment, 'id' | 'timestamp'>) =>
    api.post<{ assessment: ReachAssessment }>(`${base(projectId)}/assessments`, data),
  delete: (projectId: string, id: string) =>
    api.delete(`${base(projectId)}/assessments/${id}`)
};

// ── Pulse ──────────────────────────────────────────────────────────────────

export const reachPulseAPI = {
  list: (projectId: string) =>
    api.get<{ pulse: ReachPulseEntry[] }>(`${base(projectId)}/pulse`),
  getWeek: (projectId: string, week: number) =>
    api.get<{ entry: ReachPulseEntry }>(`${base(projectId)}/pulse/${week}`),
  putWeek: (projectId: string, week: number, data: Partial<ReachPulseEntry>) =>
    api.put(`${base(projectId)}/pulse/${week}`, data)
};

// ── Tracker ────────────────────────────────────────────────────────────────

export const reachTrackerAPI = {
  list: (projectId: string) =>
    api.get<{ tracker: ReachTrackerEntry[] }>(`${base(projectId)}/tracker`),
  putWeek: (projectId: string, week: number, data: Partial<ReachTrackerEntry>) =>
    api.put(`${base(projectId)}/tracker/${week}`, data)
};

// ── Playbook ───────────────────────────────────────────────────────────────

export const reachPlaybookAPI = {
  list: (projectId: string) =>
    api.get<{ playbook: ReachPlaybookEntry[] }>(`${base(projectId)}/playbook`),
  getWeek: (projectId: string, week: number) =>
    api.get<{ entry: ReachPlaybookEntry | null }>(`${base(projectId)}/playbook/${week}`),
  putWeek: (projectId: string, week: number, data: Partial<ReachPlaybookEntry>) =>
    api.put(`${base(projectId)}/playbook/${week}`, data),
  resetWeek: (projectId: string, week: number) =>
    api.delete(`${base(projectId)}/playbook/${week}/reset`)
};

// ── Comms ──────────────────────────────────────────────────────────────────

export const reachCommsAPI = {
  list: (projectId: string) =>
    api.get<{ comms: ReachCommsItem[] }>(`${base(projectId)}/comms`),
  create: (projectId: string, data: Omit<ReachCommsItem, 'id' | 'createdAt' | 'createdBy'>) =>
    api.post<{ item: ReachCommsItem }>(`${base(projectId)}/comms`, data),
  update: (projectId: string, id: string, data: Partial<ReachCommsItem>) =>
    api.patch<{ item: ReachCommsItem }>(`${base(projectId)}/comms/${id}`, data),
  delete: (projectId: string, id: string) =>
    api.delete(`${base(projectId)}/comms/${id}`)
};

// ── Blockers ───────────────────────────────────────────────────────────────

export const reachBlockersAPI = {
  list: (projectId: string) =>
    api.get<{ blockers: ReachBlocker[] }>(`${base(projectId)}/blockers`),
  create: (projectId: string, data: Omit<ReachBlocker, 'id' | 'createdAt' | 'updatedAt' | 'aiSuggested' | 'confirmedByPractitioner'>) =>
    api.post<{ blocker: ReachBlocker }>(`${base(projectId)}/blockers`, data),
  update: (projectId: string, id: string, data: Partial<ReachBlocker>) =>
    api.patch<{ blocker: ReachBlocker }>(`${base(projectId)}/blockers/${id}`, data),
  delete: (projectId: string, id: string) =>
    api.delete(`${base(projectId)}/blockers/${id}`),
  suggest: (projectId: string) =>
    api.post(`${base(projectId)}/blockers/suggest`)
};

// ── Extractions ────────────────────────────────────────────────────────────

export const reachExtractionsAPI = {
  list: (projectId: string) =>
    api.get<{ extractions: ReachExtraction[] }>(`${base(projectId)}/extractions`),
  get: (projectId: string, sessionId: string) =>
    api.get<{ extraction: ReachExtraction }>(`${base(projectId)}/extractions/${sessionId}`),
  trigger: (projectId: string, sessionId: string, payload: Record<string, unknown>) =>
    api.post(`${base(projectId)}/extractions/${sessionId}`, payload),
  update: (projectId: string, sessionId: string, draft: Partial<ExtractionDraft>) =>
    api.patch(`${base(projectId)}/extractions/${sessionId}`, draft),
  confirm: (projectId: string, sessionId: string) =>
    api.post(`${base(projectId)}/extractions/${sessionId}/confirm`),
  dismiss: (projectId: string, sessionId: string) =>
    api.post(`${base(projectId)}/extractions/${sessionId}/dismiss`)
};

// ── Close-Out ──────────────────────────────────────────────────────────────

export const reachCloseOutAPI = {
  get: (projectId: string) =>
    api.get(`${base(projectId)}/closeout`),
  update: (projectId: string, data: Record<string, unknown>) =>
    api.patch(`${base(projectId)}/closeout`, data),
  generate: (projectId: string) =>
    api.post(`${base(projectId)}/closeout/generate`)
};

// ── Plan ───────────────────────────────────────────────────────────────────

export const reachPlanAPI = {
  get: (projectId: string) =>
    api.get<{ plan: ReachAdoptionPlan | null }>(`${base(projectId)}/plan`),
  updateSection: (projectId: string, sectionId: string, editedContent: string) =>
    api.patch(`${base(projectId)}/plan/sections/${sectionId}`, { editedContent }),
  generate: (projectId: string, sectionId?: string) =>
    api.post<{ requestId: string; status: 'pending' }>(`${base(projectId)}/plan/generate`, { sectionId }),
  getTask: (projectId: string, requestId: string) =>
    api.get<{ status: 'pending' | 'complete' | 'failed'; error: string | null; completedAt: string | null }>(
      `${base(projectId)}/plan/task/${requestId}`
    )
};

// ── Skills config export ───────────────────────────────────────────────────

export const reachSkillsAPI = {
  downloadConfig: (projectId: string) =>
    api.get(`${base(projectId)}/skills/config-export`, { responseType: 'blob' })
};
